/**
 * WAFR review (agentic loop) endpoints — scope form, evidence sweep, item
 * confirm/answer/reopen/remediation, session chat, and the deterministic
 * report download. Mirrors backend api/wafr_review.py, mounted under
 * /api/companies/{slug}/wafr-review.
 */
import { ApiError, isApiStatus, request, requestBlob, saveBlob } from './core'
import type {
  AceSearchResponse,
  WafrFundingOutcomeStatus,
  WafrFundingRequest,
  WafrReviewMeta,
  WafrReviewState,
} from './types'

/** A publish refused for its target account (409 workload_account_required |
 *  workload_account_invalid) → an Error carrying just the server's actionable
 *  message; anything else → null (rethrown unchanged). Branches on the
 *  `ApiError`'s status + raw body, never on the message text. */
export function workloadAccountError(e: unknown): Error | null {
  if (!isApiStatus(e, 409)) return null
  try {
    const detail = JSON.parse((e as ApiError).body)?.detail
    if (typeof detail?.error === 'string' && detail.error.startsWith('workload_account_')) {
      return new Error(String(detail.message))
    }
  } catch {
    // not a JSON body — not ours
  }
  return null
}

/** Keep whichever review is newer. Poll refreshes and mutation responses race
 *  (a GET issued at revision 7 can land after a confirm already set 8), so a
 *  response with a LOWER revision than the one on screen is stale and dropped —
 *  adopting it would roll item versions back and 412 the next edit. An equal
 *  revision is adopted: session appends and evidence inserts don't bump it. */
export function newerReview(
  prev: WafrReviewState | null,
  next: WafrReviewState,
): WafrReviewState {
  return prev && next.revision < prev.revision ? prev : next
}

/** PUT /funding — only the keys sent apply (partial patch). `status` narrows to
 *  the outcome states: the endpoint 422s on 'none'. */
export type WafrFundingPatch = Partial<
  Pick<
    WafrFundingRequest,
    | 'activity_name'
    | 'business_description'
    | 'planned_start_date'
    | 'planned_end_date'
    | 'arr_usd'
    | 'fund_request_id'
    | 'notes'
  >
> & { status?: WafrFundingOutcomeStatus }

export const wafrReviewApi = {
  getWafrReview: (slug: string) =>
    request<WafrReviewState>(`/api/companies/${slug}/wafr-review`),

  /** Open ACE opportunities matching `query`. Never throws on an AWS-side error —
   *  the message comes back in `error` for the modal to show inline. */
  searchWafrAce: (slug: string, query = '') =>
    request<AceSearchResponse>(
      `/api/companies/${slug}/wafr-review/ace/search?q=${encodeURIComponent(query)}`,
      { method: 'POST' },
    ),

  /** Record an existing opportunity on the review. */
  linkWafrAce: (slug: string, opportunityId: string, revision: number) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/ace/link`,
      { method: 'POST', body: JSON.stringify({ opportunity_id: opportunityId }) },
      { ifMatch: revision },
    ),

  /** Create a WAFR-shaped opportunity (`WAFR-<CustomerName>`, APN program
   *  `Well-Architected`, $1 MRR) and record it on the review. */
  createWafrAce: (slug: string, revision: number) =>
    request<{ opportunity_id: string; state: WafrReviewState }>(
      `/api/companies/${slug}/wafr-review/ace/opportunity`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  /** Push the published workload ARN onto the linked opportunity. */
  syncWafrAceArn: (slug: string, revision: number) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/ace/sync-arn`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  /** Manually (re-)prepare the WAFP fund request — the publish worker does this
   *  automatically once the WA Tool publish lands. 409 funding_not_ready
   *  before a workload ARN exists. */
  prepareWafrFunding: (slug: string, revision: number) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/funding/prepare`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  /** Edit the prepared fund request / record the APFP outcome (partial patch). */
  updateWafrFunding: (slug: string, patch: WafrFundingPatch, revision: number) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/funding`,
      { method: 'PUT', body: JSON.stringify(patch) },
      { ifMatch: revision },
    ),

  /** Create the review (409 if one already exists). `fields` seeds the scope
   *  fields (workload_name, solution_name, ...); the POST body IS the fields
   *  object itself, not wrapped. */
  createWafrReview: (slug: string, fields?: Record<string, unknown>) =>
    request<WafrReviewState>(`/api/companies/${slug}/wafr-review`, {
      method: 'POST',
      body: JSON.stringify(fields ?? {}),
    }),

  /** Light poll target — the workspace polls this and refetches the full
   *  review only when it changes. */
  getWafrReviewMeta: (slug: string) =>
    request<WafrReviewMeta>(`/api/companies/${slug}/wafr-review/meta`),

  /** Save changed scope fields only. */
  saveWafrReviewFields: (slug: string, fields: Record<string, unknown>, revision: number) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/fields`,
      { method: 'PUT', body: JSON.stringify({ fields }) },
      { ifMatch: revision },
    ),

  /** Kick the evidence sweep (409 while already running). `force` restarts a
   *  sweep stuck in `running` by a dead worker — the recovery path. The server
   *  accepts an empty body too, but we always send the flag. */
  kickWafrSweep: (slug: string, revision: number, force = false) =>
    request<{ sweep_status: string }>(
      `/api/companies/${slug}/wafr-review/sweep`,
      { method: 'POST', body: JSON.stringify({ force }) },
      { ifMatch: revision },
    ),

  /** Publish the review into the customer's WA Tool (202 while it runs; publish
   *  state rides GET ''). 409 {error:'publish_running'} while a publish is in
   *  flight; 409 invalid_item_state with zero answered items. `force` restarts a
   *  publish stranded in `running` by a dead worker — same escape hatch as the
   *  sweep (the WA Tool writes are idempotent: workload adopted on conflict,
   *  milestone date-named). `workloadAccountId` is the customer account the
   *  workload is created in — required when the connection has several
   *  accounts (409 workload_account_required|invalid otherwise); a single
   *  account is the default and a re-publish follows the recorded workload. */
  publishWafrToWaTool: (
    slug: string,
    revision: number,
    force = false,
    workloadAccountId?: string,
  ) =>
    request<{ wa_publish_status: string }>(
      `/api/companies/${slug}/wafr-review/wa-tool/publish`,
      {
        method: 'POST',
        body: JSON.stringify(
          workloadAccountId ? { force, workload_account_id: workloadAccountId } : { force },
        ),
      },
      { ifMatch: revision },
    ).catch((e: unknown) => {
      throw workloadAccountError(e) ?? e
    }),

  // Item mutations are ITEM-scoped: they send the `version` of the item the
  // client saw (`X-Item-Version`), not the review revision — the evidence sweep
  // and workers bump the review revision constantly, which is no conflict on
  // the item being touched. A 412 here means THIS item changed underneath.
  confirmWafrItem: (slug: string, playId: string, itemVersion: number, correctedText?: string) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/items/${playId}/confirm`,
      { method: 'POST', body: JSON.stringify({ corrected_text: correctedText ?? null }) },
      { itemVersion },
    ),

  answerWafrItem: (slug: string, playId: string, text: string, itemVersion: number) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/items/${playId}/answer`,
      { method: 'POST', body: JSON.stringify({ text }) },
      { itemVersion },
    ),

  wafrRemediationDone: (slug: string, playId: string, itemVersion: number) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/items/${playId}/remediation-done`,
      { method: 'POST' },
      { itemVersion },
    ),

  /** Answer a clarifying question the agent raised on an item (appends a Q/A
   *  block to the item's notes). */
  answerWafrClarification: (
    slug: string,
    playId: string,
    clarificationId: string,
    text: string,
    itemVersion: number,
  ) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/items/${playId}/clarifications/${clarificationId}/answer`,
      { method: 'POST', body: JSON.stringify({ text }) },
      { itemVersion },
    ),

  /** Skip (remove) a clarifying question — no answer needed. */
  skipWafrClarification: (
    slug: string,
    playId: string,
    clarificationId: string,
    itemVersion: number,
  ) =>
    request<WafrReviewState>(
      `/api/companies/${slug}/wafr-review/items/${playId}/clarifications/${clarificationId}/skip`,
      { method: 'POST' },
      { itemVersion },
    ),

  /** Run the advisory analysis (202 while it runs; result rides GET ''; 409
   *  {error:'analysis_running'} while one is in flight). Never a gate. */
  kickWafrAnalysis: (slug: string, revision: number) =>
    request<{ analysis_status: string }>(
      `/api/companies/${slug}/wafr-review/analysis`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  /** Queue the agent's opening session turn (no consultant message) — the
   *  agent leads with a contradiction to address or the first open question.
   *  Idempotent server-side (a non-empty transcript / in-flight turn → noop),
   *  so it takes no revision at all. */
  kickWafrSession: (slug: string) =>
    request<{ status: string }>(`/api/companies/${slug}/wafr-review/session/kickoff`, {
      method: 'POST',
    }),

  /** Send a consultant chat message (409 while a turn is already running). */
  sendWafrSessionMessage: (slug: string, message: string, revision: number) =>
    request<{ status: string }>(
      `/api/companies/${slug}/wafr-review/session/messages`,
      { method: 'POST', body: JSON.stringify({ message }) },
      { ifMatch: revision },
    ),

  /** Download the deterministic WAFR report docx and trigger a save. */
  downloadWafrReport: async (slug: string): Promise<void> => {
    const blob = await requestBlob(`/api/companies/${slug}/wafr-review/report.docx`)
    saveBlob(blob, `${slug}-wafr-review.docx`)
  },
}
